import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { apiClient, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import BeneficiariosEditor from "@/components/BeneficiariosEditor";

const LOGO = "/angel-logo.png";

const PLANES = [
  { id: "basico",   nombre: "Plan Básico",   precio: "4.99",  regular: "8.00",  max: 1, features: ["1 grúa al año","Servicio médico telefónico","Soporte por chat"] },
  { id: "estandar", nombre: "Plan Estándar", precio: "9.99",  regular: "20.00", max: 4, featured: true, features: ["Incluye hasta 4 personas","3 grúas al año","Servicio médico 24/7","Ambulancia 1/año","Servicio legal"] },
];

const STEPS = [
  { id: 1, title: "Cuenta", desc: "Datos del titular" },
  { id: 2, title: "Beneficiarios", desc: "Familiares en México" },
  { id: 3, title: "Plan y pago", desc: "Activa tu membresía" },
];

export default function Onboarding() {
  const { user, setUser, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const beneficiariosCount = user?.beneficiarios?.length || 0;
  const isActive = user?.membership_status === "active";
  // Determine current step
  const initialStep = isActive ? 3 : (beneficiariosCount > 0 ? 3 : 2);
  const [step, setStep] = useState(initialStep);
  const [loadingPlan, setLoadingPlan] = useState(null);

  useEffect(() => {
    if (isActive) {
      toast.success("¡Tu membresía ya está activa!");
      navigate("/cuenta");
    }
  }, [isActive, navigate]);

  const handleBenefSaved = (updatedUser) => {
    setUser(updatedUser);
    setStep(3);
  };

  const elegirPlan = async (plan) => {
    if (!user?.beneficiarios?.length) {
      toast.error("Primero agrega al menos un beneficiario.");
      setStep(2);
      return;
    }
    if (user.beneficiarios.length > plan.max) {
      toast.error(`Este plan permite máximo ${plan.max} beneficiario(s). Tienes ${user.beneficiarios.length}.`);
      return;
    }
    setLoadingPlan(plan.id);
    try {
      const { data } = await apiClient.post("/checkout/session", { plan_id: plan.id, origin_url: window.location.origin });
      trackEvent("begin_checkout", {
        currency: "USD",
        value: parseFloat(plan.precio),
        items: [{ item_id: plan.id, item_name: plan.nombre, price: parseFloat(plan.precio), quantity: 1 }],
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoadingPlan(null);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const maxBenef = 4; // Pre-payment: allow max across all plans

  return (
    <div className="onboard-page">
      <header className="onboard-header">
        <a href="/"><img src={LOGO} alt="Mi Ángel en Casa" className="onboard-logo" /></a>
        <button onClick={handleLogout} className="onboard-logout" data-testid="onboard-logout">
          <i className="fas fa-sign-out-alt" /> Salir
        </button>
      </header>

      <div className="onboard-container">
        {/* Stepper */}
        <div className="stepper" data-testid="stepper">
          {STEPS.map((s, i) => {
            const completed = s.id < step || (s.id === 1) || (s.id === 2 && beneficiariosCount > 0) || (s.id === 3 && isActive);
            const active = s.id === step;
            return (
              <div key={s.id} className={`step ${active ? "step-active" : ""} ${completed ? "step-done" : ""}`} data-testid={`step-${s.id}`}>
                <div className="step-circle">
                  {completed && !active ? <i className="fas fa-check" /> : s.id}
                </div>
                <div className="step-meta">
                  <strong>{s.title}</strong>
                  <span>{s.desc}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`step-line ${completed ? "step-line-done" : ""}`} />}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="onboard-panel">
          {step === 1 && (
            <div className="onboard-step" data-testid="onboard-step-1">
              <h2><i className="fas fa-check-circle" style={{ color: "#7DC242", marginRight: 8 }} /> Cuenta creada</h2>
              <p>¡Bienvenido <strong>{user?.nombre}</strong>! Tu cuenta ya está lista. Continúa con los datos de las personas en México que vas a proteger.</p>
              <button className="btn btn-primary" onClick={() => setStep(2)} data-testid="continue-step-2">
                Continuar <i className="fas fa-arrow-right" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="onboard-step" data-testid="onboard-step-2">
              <h2>Tus beneficiarios en México</h2>
              <p className="text-muted">Datos críticos para poder despachar grúa, ambulancia o legal cuando lo necesiten. Puedes agregar hasta 4 personas (depende del plan).</p>
              <BeneficiariosEditor user={user} maxAllowed={maxBenef} onSaved={handleBenefSaved} />
              <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between" }}>
                <button className="btn btn-secondary" onClick={() => setStep(1)} data-testid="back-step-1">
                  <i className="fas fa-arrow-left" /> Atrás
                </button>
                {beneficiariosCount > 0 && (
                  <button className="btn btn-primary" onClick={() => setStep(3)} data-testid="continue-step-3">
                    Continuar al plan <i className="fas fa-arrow-right" />
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="onboard-step" data-testid="onboard-step-3">
              <h2>Elige tu plan</h2>
              <p className="text-muted">Activa tu membresía con cobro membresía transfronteriza vía Stripe. Puedes cancelar cuando quieras.</p>

              {beneficiariosCount === 0 && (
                <div className="alert-card" style={{ marginBottom: 16 }}>
                  <i className="fas fa-exclamation-triangle" />
                  <div>
                    <strong>Primero agrega un beneficiario.</strong>
                    <p>Sin esto no podemos enviar servicio a nadie en México.</p>
                  </div>
                  <button className="btn btn-primary" onClick={() => setStep(2)}>Ir al paso 2</button>
                </div>
              )}

              <div className="planes-grid">
                {PLANES.map((p) => {
                  const disabledByLimit = beneficiariosCount > p.max;
                  return (
                    <div key={p.id} className={`plan-card ${p.featured ? "featured" : ""}`} data-testid={`onboard-plan-${p.id}`}>
                      {p.featured && <div className="plan-badge">MÁS POPULAR</div>}
                      <div className="plan-header"><h3>{p.nombre}</h3></div>
                      <div style={{ textAlign: "center", marginTop: 8 }}>
                        <span style={{ display: "inline-block", background: "#E8F5D9", color: "#5BA628", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>PROMO ANUAL</span>
                        <div style={{ color: "#E84C5E", textDecoration: "line-through", fontWeight: 600, fontSize: 16, marginTop: 4 }}>USD ${p.regular}/mes</div>
                      </div>
                      <div className="plan-price"><span className="currency">USD $</span><span className="amount">{p.precio}</span><span className="period">/mes</span></div>
                      <p style={{ textAlign: "center", fontSize: 12, color: p.max >= beneficiariosCount ? "#5BA628" : "#E84C5E", margin: "4px 0 8px" }}>
                        Permite hasta {p.max} beneficiario(s) · Tienes {beneficiariosCount}
                      </p>
                      <ul className="plan-features">{p.features.map((f, i) => <li key={i}><i className="fas fa-check" />{f}</li>)}</ul>
                      <button
                        onClick={() => elegirPlan(p)}
                        disabled={loadingPlan === p.id || disabledByLimit || beneficiariosCount === 0}
                        className="btn btn-primary plan-btn"
                        data-testid={`onboard-elegir-${p.id}`}>
                        {loadingPlan === p.id ? "Procesando..." : disabledByLimit ? "Excede beneficiarios" : "Elegir y pagar"}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 16 }}>
                <button className="btn btn-secondary" onClick={() => setStep(2)} data-testid="back-step-2">
                  <i className="fas fa-arrow-left" /> Atrás
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="onboard-skip">
          ¿Prefieres explorar primero? <button onClick={() => navigate("/cuenta")} className="link-btn" data-testid="skip-onboarding">Ir al portal sin activar</button>
        </p>
      </div>
    </div>
  );
}
